import { useCallback, useRef, useState } from 'react'
import type { JSX } from 'react'

import { Dialog, Flex, Heading } from '@buffer-mono/popcorn'

import { CsvUploadStep } from '../CsvUploadStep/CsvUploadStep'
import { ChannelMappingStep } from '../ChannelMappingStep/ChannelMappingStep'
import { ReviewStep } from '../ReviewStep/ReviewStep'
import { ImportExecutionStep } from '../ImportExecutionStep/ImportExecutionStep'
import { SOURCE_LABELS } from './types'
import type {
  ChannelMapping,
  ImportItem,
  ImportItemStatus,
  ImportSession,
  MigrationSource,
  WizardStep,
} from './types'
import {
  clearSession,
  deserializeItems,
  saveSession,
  serializeItems,
} from './importSession'

import styles from './MigrationWizard.module.css'

type MigrationWizardProps = {
  source: MigrationSource
  resumeSession?: ImportSession | null
  onClose: () => void
}

export function MigrationWizard({
  source,
  resumeSession,
  onClose,
}: MigrationWizardProps): JSX.Element {
  const [step, setStep] = useState<WizardStep>(resumeSession?.step ?? 'upload')
  const [items, setItems] = useState<ImportItem[]>(
    resumeSession ? deserializeItems(resumeSession.items) : [],
  )
  const [channelMappings, setChannelMappings] = useState<ChannelMapping[]>(
    resumeSession?.channelMappings ?? [],
  )
  const [timezone, setTimezone] = useState<string>(
    resumeSession?.timezone ?? '',
  )
  const sessionIdRef = useRef(resumeSession?.id ?? crypto.randomUUID())

  const sourceLabel = SOURCE_LABELS[source]

  const persistSession = useCallback(
    (
      overrides: Partial<{
        step: WizardStep
        items: ImportItem[]
        channelMappings: ChannelMapping[]
        timezone: string
      }> = {},
    ) => {
      const session: ImportSession = {
        id: sessionIdRef.current,
        source,
        startedAt: resumeSession?.startedAt ?? new Date().toISOString(),
        timezone: overrides.timezone ?? timezone,
        channelMappings: overrides.channelMappings ?? channelMappings,
        items: serializeItems(overrides.items ?? items),
        step: overrides.step ?? step,
      }
      saveSession(session)
    },
    [source, resumeSession?.startedAt, timezone, channelMappings, items, step],
  )

  const handleSaveProgress = useCallback(
    (statuses: Record<string, ImportItemStatus>) => {
      // Persist full items with updated statuses directly to localStorage.
      // Do NOT call setItems here — that would trigger a re-render, recreate
      // the start() callback, and abort the running import via the cleanup effect.
      const updatedItems = items.map((item) => ({
        ...item,
        status: statuses[item.id] ?? item.status,
      }))
      persistSession({ items: updatedItems, step: 'importing' })
    },
    [items, persistSession],
  )

  const handleCsvParsed = (
    parsedItems: ImportItem[],
    selectedTimezone: string,
  ): void => {
    setItems(parsedItems)
    setTimezone(selectedTimezone)
    setStep('channel-mapping')
    persistSession({
      step: 'channel-mapping',
      items: parsedItems,
      timezone: selectedTimezone,
    })
  }

  const handleMappingComplete = (mappings: ChannelMapping[]): void => {
    setChannelMappings(mappings)
    setStep('review')
    persistSession({ step: 'review', channelMappings: mappings })
  }

  const handleStartImport = (): void => {
    setStep('importing')
    persistSession({ step: 'importing' })
  }

  const [showDismissPrompt, setShowDismissPrompt] = useState(false)

  const handleImportClose = useCallback(() => {
    clearSession()
    onClose()
  }, [onClose])

  const handleContinueLater = useCallback(() => {
    // Session is already persisted — just close the dialog
    setShowDismissPrompt(false)
    onClose()
  }, [onClose])

  const handleAbortImport = useCallback(() => {
    clearSession()
    setShowDismissPrompt(false)
    onClose()
  }, [onClose])

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        if (step === 'importing') {
          setShowDismissPrompt(true)
        } else {
          onClose()
        }
      }
    },
    [step, onClose],
  )

  const stepTitle: Record<WizardStep, string> = {
    upload: `Import from ${sourceLabel}`,
    'channel-mapping': 'Map your channels',
    review: 'Review import',
    importing: 'Importing...',
  }

  return (
    <Dialog open onOpenChange={handleDialogOpenChange}>
      <Dialog.Content
        className={styles.dialog}
        aria-label={`Import from ${sourceLabel}`}
      >
        <Dialog.Header>
          <Dialog.Title>
            <Flex align="center" gap="xs">
              <Heading size="small">{stepTitle[step]}</Heading>
            </Flex>
          </Dialog.Title>
        </Dialog.Header>
        <Dialog.Body className={styles.body}>
          {step === 'upload' && (
            <CsvUploadStep source={source} onParsed={handleCsvParsed} />
          )}
          {step === 'channel-mapping' && (
            <ChannelMappingStep
              items={items}
              onComplete={handleMappingComplete}
              onBack={() => setStep('upload')}
            />
          )}
          {step === 'review' && (
            <ReviewStep
              items={items}
              channelMappings={channelMappings}
              timezone={timezone}
              onStartImport={handleStartImport}
              onBack={() => setStep('channel-mapping')}
            />
          )}
          {step === 'importing' && (
            <ImportExecutionStep
              items={items}
              channelMappings={channelMappings}
              timezone={timezone}
              onSaveProgress={handleSaveProgress}
              onClose={handleImportClose}
              onAbort={handleAbortImport}
              onContinueLater={handleContinueLater}
              showDismissPrompt={showDismissPrompt}
              onDismissPromptClose={() => setShowDismissPrompt(false)}
            />
          )}
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  )
}
